/**
 * Authentication Service Layer
 * REG-007: Centralized authentication, registration, and user management
 * 
 * This service provides a clean, testable API for all auth operations.
 * All auth logic is centralized here, keeping components focused on UI.
 */

import { supabase } from '@/integrations/supabase/client';
import type { 
  Result, 
  RegistrationData, 
  ProfileData, 
  ProfileResult,
  VerificationOptions,
  ResendVerificationOptions
} from './auth.types';
import { 
  mapSupabaseError, 
  isClientError, 
  isRateLimitError,
  ERROR_MESSAGES 
} from './auth.errors';
import { validateSignupData } from './validation.service';
import { checkCompanyNameExists } from './organization.service';

/**
 * AuthService class - Singleton pattern
 * Handles all authentication operations with consistent error handling
 */
class AuthService {
  private maxRetries = 3;
  private retryDelay = 1000; // 1 second base delay
  private resendCooldown = new Map<string, number>(); // Email -> timestamp

  /**
   * Initiate user registration with email and password
   * Performs server-side validation and creates auth user
   * 
   * @param data - Registration data including email, password, names, and reCAPTCHA token
   * @returns Result with void on success or error on failure
   */
  async initiateRegistration(data: RegistrationData): Promise<Result<void>> {
    try {
      // Step 1: Server-side validation via Edge Function
      const validationResult = await validateSignupData({
        email: data.email,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        recaptchaToken: data.recaptchaToken
      });

      // Handle validation errors
      if (!validationResult.success && 'validationErrors' in validationResult) {
        const firstError = Object.values(validationResult.validationErrors)[0];
        return {
          success: false,
          error: {
            code: 'VALIDATION_ERROR',
            message: validationResult.error || 'Validation failed',
            userMessage: Array.isArray(firstError) ? firstError[0] : 'Validation failed',
            details: { validationErrors: validationResult.validationErrors }
          }
        };
      }

      // Step 2: Create Supabase auth user with retry logic
      const signUpResult = await this.retryOperation(async () => {
        const redirectUrl = `${window.location.origin}/auth/verify-email`;
        
        return await supabase.auth.signUp({
          email: data.email,
          password: data.password,
          options: {
            emailRedirectTo: redirectUrl,
            data: {
              first_name: data.firstName,
              last_name: data.lastName
            }
          }
        });
      });

      if (signUpResult.error) {
        return {
          success: false,
          error: mapSupabaseError(signUpResult.error)
        };
      }

      return { success: true, data: undefined };
    } catch (error) {
      // Handle rate limit errors specially
      if (isRateLimitError(error)) {
        return {
          success: false,
          error: {
            code: 'RATE_LIMIT',
            message: 'Rate limit exceeded',
            userMessage: ERROR_MESSAGES['rate-limit-exceeded']
          }
        };
      }

      return {
        success: false,
        error: mapSupabaseError(error)
      };
    }
  }

  /**
   * Complete user profile after email verification
   * Creates profile record and optionally company record
   * 
   * @param data - Profile data including role, names, and company info
   * @returns Result with ProfileResult on success
   */
  async completeProfile(data: ProfileData): Promise<Result<ProfileResult>> {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        return {
          success: false,
          error: {
            code: 'AUTH_ERROR',
            message: 'User not authenticated',
            userMessage: 'Please sign in to complete your profile.'
          }
        };
      }

      // Check email verification
      if (!user.email_confirmed_at) {
        return {
          success: false,
          error: {
            code: 'EMAIL_NOT_VERIFIED',
            message: 'Email not verified',
            userMessage: ERROR_MESSAGES['auth/email-not-verified']
          }
        };
      }

      // Check for duplicate company name if provided
      if (data.company_name) {
        const companyCheck = await checkCompanyNameExists(data.company_name);
        if (companyCheck.exists) {
          return {
            success: false,
            error: {
              code: 'COMPANY_DUPLICATE',
              message: 'Company name already exists',
              userMessage: companyCheck.message || ERROR_MESSAGES['company-duplicate']
            }
          };
        }
      }

      // Create profile with retry logic
      const profileResult = await this.retryOperation(async () => {
        return await supabase
          .from('profiles')
          .insert({
            user_id: user.id,
            role: data.role,
            legal_first_name: data.legal_first_name,
            legal_last_name: data.legal_last_name,
            onboarding_completed: true
          })
          .select()
          .single();
      });

      if (profileResult.error) {
        return {
          success: false,
          error: mapSupabaseError(profileResult.error)
        };
      }

      // Create company if business owner
      let companyId: string | undefined;
      if (data.role === 'business_owner' && data.company_name) {
        const companyResult = await this.retryOperation(async () => {
          return await supabase
            .from('organizations')
            .insert({
              name: data.company_name!,
              owner_id: user.id,
              business_structure: data.business_structure,
              industry: data.industry
            })
            .select()
            .single();
        });

        if (companyResult.error) {
          // Rollback profile creation (best effort)
          await supabase
            .from('profiles')
            .delete()
            .eq('user_id', user.id);
          
          return {
            success: false,
            error: mapSupabaseError(companyResult.error)
          };
        }

        companyId = companyResult.data.id;
      }

      return {
        success: true,
        data: {
          userId: user.id,
          profileId: profileResult.data.id,
          companyId,
          emailVerified: true
        }
      };
    } catch (error) {
      return {
        success: false,
        error: mapSupabaseError(error)
      };
    }
  }

  /**
   * Verify email using token from verification link
   * 
   * @param options - Verification options with token hash and type
   * @returns Result with void on success
   */
  async verifyEmail(options: VerificationOptions): Promise<Result<void>> {
    try {
      const verifyResult = await this.retryOperation(async () => {
        return await supabase.auth.verifyOtp({
          token_hash: options.tokenHash,
          type: options.type
        });
      });

      if (verifyResult.error) {
        // Map specific verification errors
        const error = verifyResult.error;
        if (error.message?.includes('expired')) {
          return {
            success: false,
            error: {
              code: 'TOKEN_EXPIRED',
              message: error.message,
              userMessage: ERROR_MESSAGES['token-expired']
            }
          };
        }
        
        if (error.message?.includes('invalid')) {
          return {
            success: false,
            error: {
              code: 'TOKEN_INVALID',
              message: error.message,
              userMessage: ERROR_MESSAGES['token-invalid']
            }
          };
        }

        return {
          success: false,
          error: mapSupabaseError(error)
        };
      }

      return { success: true, data: undefined };
    } catch (error) {
      return {
        success: false,
        error: mapSupabaseError(error)
      };
    }
  }

  /**
   * Resend verification email with rate limiting
   * Enforces 60-second cooldown between resend attempts
   * 
   * @param options - Resend options with email and redirect URL
   * @returns Result with void on success
   */
  async resendVerificationEmail(options: ResendVerificationOptions): Promise<Result<void>> {
    try {
      // Check cooldown
      const lastSent = this.resendCooldown.get(options.email);
      const now = Date.now();
      const cooldownPeriod = 60000; // 60 seconds

      if (lastSent && now - lastSent < cooldownPeriod) {
        const remainingSeconds = Math.ceil((cooldownPeriod - (now - lastSent)) / 1000);
        return {
          success: false,
          error: {
            code: 'RATE_LIMIT',
            message: 'Rate limit exceeded',
            userMessage: `Please wait ${remainingSeconds} seconds before resending.`
          }
        };
      }

      // Resend verification email
      const resendResult = await this.retryOperation(async () => {
        return await supabase.auth.resend({
          type: 'signup',
          email: options.email,
          options: {
            emailRedirectTo: options.emailRedirectTo || `${window.location.origin}/auth/verify-email`
          }
        });
      });

      if (resendResult.error) {
        return {
          success: false,
          error: mapSupabaseError(resendResult.error)
        };
      }

      // Update cooldown
      this.resendCooldown.set(options.email, now);

      // Clean up old cooldown entries (older than 2 minutes)
      for (const [email, timestamp] of this.resendCooldown.entries()) {
        if (now - timestamp > 120000) {
          this.resendCooldown.delete(email);
        }
      }

      return { success: true, data: undefined };
    } catch (error) {
      return {
        success: false,
        error: mapSupabaseError(error)
      };
    }
  }

  /**
   * Check if current user's email is verified
   * 
   * @returns Result with boolean indicating verification status
   */
  async checkEmailVerificationStatus(): Promise<Result<boolean>> {
    try {
      const { data: { user }, error } = await supabase.auth.getUser();
      
      if (error) {
        return {
          success: false,
          error: mapSupabaseError(error)
        };
      }

      return {
        success: true,
        data: !!user?.email_confirmed_at
      };
    } catch (error) {
      return {
        success: false,
        error: mapSupabaseError(error)
      };
    }
  }

  /**
   * Check if company name already exists in the system
   * 
   * @param name - Company name to check
   * @returns Result with boolean indicating if company exists
   */
  async checkCompanyExists(name: string): Promise<Result<boolean>> {
    try {
      const result = await checkCompanyNameExists(name);
      return {
        success: true,
        data: result.exists
      };
    } catch (error) {
      return {
        success: false,
        error: mapSupabaseError(error)
      };
    }
  }

  /**
   * Retry operation with exponential backoff
   * Only retries on transient errors (network, server errors)
   * Does not retry on client errors (validation, auth failures)
   * 
   * @private
   */
  private async retryOperation<T>(
    operation: () => Promise<T>
  ): Promise<T> {
    let lastError: unknown;

    for (let attempt = 0; attempt < this.maxRetries; attempt++) {
      try {
        return await operation();
      } catch (error) {
        lastError = error;

        // Don't retry on client errors (4xx)
        if (isClientError(error)) {
          throw error;
        }

        // Don't retry on last attempt
        if (attempt === this.maxRetries - 1) {
          throw error;
        }

        // Wait with exponential backoff + jitter
        const delay = this.retryDelay * Math.pow(2, attempt);
        const jitter = Math.random() * 1000;
        await new Promise(resolve => setTimeout(resolve, delay + jitter));
      }
    }

    throw lastError;
  }
}

// Export singleton instance
export const authService = new AuthService();
